import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Constants } from 'src/@smartmanufacturing/common/constants';
import { SortDirection } from 'src/@smartmanufacturing/common/enum';
import { DataMapper } from 'src/app/user-management/shared/models/data-mapper';
import {
  User,
  UserDetails,
  UserPlantsSwitchAccount,
  UserProfile,
  UsersList,
} from 'src/app/user-management/user-management-setup/models/user';
import { BaseApi } from 'src/backend/api/base-api';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(private baseApi: BaseApi, private http: HttpClient) {}

  getLazyLoadedUsersByPlant(data: any): Observable<DataMapper<User>> {
    let params = '';
    if (data?.pageIndex) {
      params += 'offset=' + data?.pageIndex.toString();
    }
    if (data?.pageSize) {
      if (params.length > 0) {
        params += '&';
      }
      params += 'limit=' + data?.pageSize.toString();
    }
    if (data?.status) {
      if (params.length > 0) {
        params += '&';
      }
      params += `status=` + data?.status;
    }
    if (data?.verifications) {
      if (params.length > 0) {
        params += '&';
      }
      params += `verifications=` + data?.verifications;
    }
    if (data?.sortDirection !== SortDirection.None) {
      if (params.length > 0) {
        params += '&';
      }
      params += 'sortDirection=' + data?.sortDirection;
    }
    if (data?.sortField) {
      if (params.length > 0) {
        params += '&';
      }
      params += 'sortField=' + data?.sortField;
    }
    if (data?.searchValue) {
      if (params.length > 0) {
        params += '&';
      }
      params += 'phrase=' + data?.searchValue;
    }
    return this.http.get<DataMapper<User>>(
      this.baseApi.encodeURL(Constants.API_USERS_LAZYLOADED + '?' + params)
    );
  }
  getUserProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(
      this.baseApi.encodeURL(Constants.API_USER_PROFILE)
    );
  }
  getUsersByPlant(): Observable<UsersList[]> {
    return this.http.get<UsersList[]>(
      this.baseApi.encodeURL(Constants.API_USERS_BY_PLANT)
    );
  }
  changeUserPhoto(data: any, userCode: string): Observable<any> {
    return this.http.put<any>(
      this.baseApi.encodeURL(Constants.API_USER_CHANGE_PHOTO + '/' + userCode),
      data
    );
  }
  createUser(data: any): Observable<any> {
    return this.http.post<any>(
      this.baseApi.encodeURL(Constants.API_USER_CREATE),
      data
    );
  }
  updateUser(data: any): Observable<any> {
    return this.http.put<any>(
      this.baseApi.encodeURL(Constants.API_USER_UPDATE),
      data
    );
  }

  activateUser(code: string): Observable<any> {
    return this.http.put<any>(
      this.baseApi.encodeURL(Constants.API_USER_ACTIVATE + '/' + code),
      {}
    );
  }
  deactivateUser(code: string): Observable<any> {
    return this.http.put<any>(
      this.baseApi.encodeURL(Constants.API_USER_DEACTIVATE + '/' + code),
      {}
    );
  }

  deleteUser(code: string): Observable<any> {
    return this.http.delete<any>(
      this.baseApi.encodeURL(Constants.API_USER_DELETE + '/' + code)
    );
  }

  getUserDetails(id: string): Observable<UserDetails> {
    return this.http.get<UserDetails>(
      this.baseApi.encodeURL(Constants.API_USER_DETAILS + '/' + id)
    );
  }
  getUserPlantsForSwitchAccount(
    code: string
  ): Observable<UserPlantsSwitchAccount[]> {
    return this.http.get<UserPlantsSwitchAccount[]>(
      this.baseApi.encodeURL(
        Constants.API_USER_PLANTS_SWITCH_ACCOUNT + '/' + code
      )
    );
  }

  savePermissions(permissions: any, userCode: string): Observable<any> {
    return this.http.put<any>(
      this.baseApi.encodeURL(
        Constants.API_USER_SAVE_PERMISSIONS + '/' + userCode
      ),
      permissions
    );
  }

  getDeletedUsers(data: any): Observable<DataMapper<UsersList>> {
    let params = '';
    if (data?.pageIndex) {
      params += 'offset=' + data?.pageIndex.toString();
    }
    if (data?.pageSize) {
      if (params.length > 0) {
        params += '&';
      }
      params += 'limit=' + data?.pageSize.toString();
    }
    if (data?.status) {
      if (params.length > 0) {
        params += '&';
      }
      params += `status=` + data?.status;
    }
    if (data?.sortDirection !== SortDirection.None) {
      if (params.length > 0) {
        params += '&';
      }
      params += 'sortDirection=' + data?.sortDirection;
    }
    if (data?.sortField) {
      if (params.length > 0) {
        params += '&';
      }
      params += 'sortField=' + data?.sortField;
    }
    if (data?.searchValue) {
      if (params.length > 0) {
        params += '&';
      }
      params += 'phrase=' + data?.searchValue;
    }
    return this.http.get<DataMapper<UsersList>>(
      this.baseApi.encodeURL(Constants.API_DELETED_USERS + '?' + params)
    );
  }

  reverseDeleteUser(code: string): Observable<any> {
    return this.http.put<any>(
      this.baseApi.encodeURL(Constants.API_DELETED_USERS_REVERSE + '/' + code),
      {}
    );
  }

  getUserConfiguartion(data: any): Observable<any> {
    return this.http.post<any>(
      this.baseApi.encodeURL(Constants.API_USER_GET_CONFIGURATION),
      data
    );
  }

  createUserConfiguartion(data: any): Observable<any> {
    return this.http.post<any>(
      this.baseApi.encodeURL(Constants.API_USER_CREATE_CONFIGURATION),
      data
    );
  }
  updateUserConfiguartion(data: any): Observable<any> {
    return this.http.post<any>(
      this.baseApi.encodeURL(Constants.API_USER_UPDATE_CONFIGURATION),
      data
    );
  }
  resetUserConfiguartion(data: any): Observable<any> {
    return this.http.post<any>(
      this.baseApi.encodeURL(Constants.API_USER_RESET_CONFIGURATION),
      data
    );
  }
}
